import type Geometry from 'ol/geom/Geometry';
import JSZip from 'jszip';
import type { AppStore } from '../core/store';
import type { AppState, MapTool } from '../core/types';
import type { SubareaParent } from '../domain/operationalSubareas';
import {
  DRAW_STALE_MESSAGE, drawBasis, drawBasisIsCurrent, drawnReference, OUTPUT_CRS, resolveTopographySelection, selectionAreaHa, selectionReferences,
  topographyKindLabel, topographyPackage, topographySummary, type DrawBasis, type DrawnReference, type TopographyPackage, type TopographySummary,
} from '../export/topographyExport';
import type { DrawingController, PartialDrawingTool } from '../map/DrawingController';
import type { MapController } from '../map/MapController';

const esc = (value: unknown): string => String(value ?? '').replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char] ?? char);
const ha = (value: number): string => `${value.toLocaleString('es-NI', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} ha`;
const facts = (rows: readonly (readonly [string, string, string?])[]): string => rows.map(([term, value, id]) => `<div><dt>${esc(term)}</dt><dd${id ? ` id="${id}"` : ''}>${esc(value)}</dd></div>`).join('');
/** Runs after the next paint, so the deferred area never delays the selection frame. */
const afterPaint = (run: () => void): void => { if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => setTimeout(run, 0)); else setTimeout(run, 0); };

/**
 * «Exportar referencia para Topografía» (pane Exportar). Read-only: it never writes the Maestro, subareas, fields,
 * classification, WorkScope or selection, and never marks the project dirty. The only store write is the transient
 * map tool while drawing (so drawing clicks cannot change the selection), restored afterwards. The optional
 * «Observaciones para Topografía» live only in the form fields and the downloaded files.
 */
export class TopographyExportController {
  private tool: PartialDrawingTool = 'draw-polygon';
  private parent: SubareaParent | null = null;
  private drawn: DrawnReference | null = null;
  private basis: DrawBasis | null = null;
  private areaRequest: object | null = null;
  private previousTool: MapTool | null = null;

  constructor(
    private readonly store: AppStore,
    private readonly drawing: DrawingController,
    private readonly map: MapController,
    private readonly save: (blob: Blob, filename: string) => void,
  ) {
    document.querySelector('#topo-export-selection')?.addEventListener('click', () => void this.exportSelection());
    document.querySelector('#topo-draw')?.addEventListener('click', () => this.startDrawing());
    document.querySelector('#topo-tool')?.addEventListener('click', (event) => {
      const tool = (event.target as Element).closest<HTMLElement>('[data-topo-tool]')?.dataset.topoTool as PartialDrawingTool | undefined;
      if (tool) { this.tool = tool; this.renderTool(); }
    });
    this.dialog.addEventListener('click', (event) => {
      const action = (event.target as Element).closest<HTMLElement>('[data-topo-action]')?.dataset.topoAction;
      if (action === 'cancel') this.cancel();
      else if (action === 'redraw') this.startDrawing(true);
      else if (action === 'export') void this.exportDrawn();
    });
    this.dialog.addEventListener('keydown', (event) => { if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.cancel(); } });
  }

  private get dialog(): HTMLDialogElement { return document.querySelector<HTMLDialogElement>('#topo-draw-dialog')!; }
  get isOpen(): boolean { return this.dialog.hasAttribute('open'); }
  private setStatus(text: string): void { const status = document.querySelector('#topo-status'); if (status) status.textContent = text; }

  /** Summary of what «Exportar selección» would deliver; only computed while the Exportar pane is showing. */
  render(state: AppState): void {
    const section = document.querySelector<HTMLElement>('#topo-ref');
    if (!section || section.closest<HTMLElement>('.dock-pane')?.hidden) return;
    const summary = topographySummary(state);
    const button = document.querySelector<HTMLButtonElement>('#topo-export-selection')!;
    const list = document.querySelector<HTMLElement>('#topo-facts')!; const hint = document.querySelector<HTMLElement>('#topo-hint')!;
    if ('reason' in summary) {
      button.disabled = true; list.hidden = true; hint.hidden = false; hint.textContent = summary.reason;
    } else {
      button.disabled = this.isOpen; hint.hidden = true; list.hidden = false;
      // Area of the geometry that will be delivered (union per entity): immediate when cached, otherwise after paint.
      const area = selectionAreaHa(state, summary.selection, { cachedOnly: true });
      list.innerHTML = facts([['Referencia', summary.label], ['Nivel', topographyKindLabel(summary.kind, summary.count)], ['Cantidad', summary.count.toLocaleString('es-NI')], ['Área geométrica activa', area === null ? 'Calculando…' : ha(area), 'topo-area'], ['Coordenadas de salida', OUTPUT_CRS], ['Archivos', 'GeoJSON + CSV (.zip)']]);
      this.areaRequest = null; if (area === null) this.scheduleArea(summary);
    }
    const parent = this.drawParent(state);
    document.querySelector<HTMLButtonElement>('#topo-draw')!.disabled = !parent || this.isOpen;
    this.renderTool();
  }

  private scheduleArea(summary: TopographySummary): void {
    const request = {}; this.areaRequest = request;
    afterPaint(() => {
      if (this.areaRequest !== request) return;
      this.areaRequest = null;
      const state = this.store.getState(); const cell = document.querySelector('#topo-area');
      if (!cell || topographySummary(state) !== summary) return;
      try { cell.textContent = ha(selectionAreaHa(state, summary.selection) ?? 0); } catch { cell.textContent = '—'; }
    });
  }

  private renderTool(): void {
    document.querySelectorAll<HTMLElement>('[data-topo-tool]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.topoTool === this.tool)));
  }

  /** Exactly one Suerte or one Tablón of the Maestro: the explicit parent the drawing is clipped to. */
  private drawParent(state: AppState): SubareaParent | null {
    const summary = topographySummary(state);
    if ('reason' in summary || summary.selection.items.length !== 1) return null;
    const item = summary.selection.items[0]!;
    return item.kind === 'suerte' || item.kind === 'tablon' ? { level: item.kind, id: item.id } : null;
  }

  private async exportSelection(): Promise<void> {
    // Revalidated against the CURRENT store (selection, DisplaySet, data) right before preparing the files.
    const state = this.store.getState(); const selection = resolveTopographySelection(state);
    if (!selection.ok) { this.setStatus(selection.reason); this.render(state); return; }
    const note = document.querySelector<HTMLTextAreaElement>('#topo-note');
    try {
      await this.download(topographyPackage(selectionReferences(state, selection, new Date().toISOString()), { note: note?.value }));
      if (note) note.value = '';
    } catch (error) {
      this.setStatus(error instanceof Error ? error.message : 'No fue posible preparar la referencia.');
    }
  }

  private startDrawing(redraw = false): void {
    const state = this.store.getState();
    if (!redraw) this.parent = this.drawParent(state);
    if (!this.parent || !state.model) { this.setStatus('Selecciona una sola Suerte o un solo Tablón para dibujar la referencia.'); return; }
    this.drawn = null; this.basis = null; this.map.setPartialAreaPreview(null);
    const entity = this.parent.level === 'suerte' ? state.model.suertes.get(this.parent.id) : state.model.tablones.get(this.parent.id);
    const label = this.parent.level === 'suerte' ? `Suerte ${(entity as { label?: string } | undefined)?.label ?? ''}` : `Tablón ${(entity as { code?: string } | undefined)?.code ?? ''}`;
    document.querySelector('#topo-draw-heading')!.textContent = `Referencia dibujada · ${label}`;
    document.querySelector('#topo-drawing-note')!.textContent = `Dibuja la superficie a comunicar (${this.tool === 'draw-rectangle' ? 'rectángulo: dos clics' : 'polígono: doble clic para terminar'}). Se recorta a la geometría activa de la ${label}. Esc para cancelar.`;
    this.showStep('drawing');
    if (!this.isOpen) { if (typeof this.dialog.show === 'function') this.dialog.show(); else this.dialog.setAttribute('open', ''); }
    if (window.matchMedia?.('(max-width: 760px)').matches) document.querySelector<HTMLElement>('[data-mobile-close]')?.click();
    this.fitParent();
    if (this.previousTool === null) this.previousTool = state.activeTool;
    this.store.update({ activeTool: 'navigate' }, { markDirty: false });
    this.drawing.capturePolygon(this.tool, (geometry) => this.review(geometry), { cancel: () => { if (this.isOpen && this.dialog.dataset.step === 'drawing') this.cancel(); } });
    this.render(this.store.getState());
  }

  private review(geometry: Geometry): void {
    const state = this.store.getState(); const model = state.model;
    if (!model || !this.parent) return;
    const warning = document.querySelector<HTMLElement>('#topo-review-warning')!;
    try {
      this.drawn = drawnReference(model, this.parent, geometry, new Date().toISOString());
      this.basis = drawBasis(state, this.parent, geometry);
    } catch (error) {
      this.drawn = null; this.basis = null; this.showStep('review');
      document.querySelector('#topo-review')!.innerHTML = '';
      warning.textContent = error instanceof Error ? error.message : 'No fue posible recortar el dibujo.';
      this.dialog.querySelector<HTMLButtonElement>('[data-topo-action="export"]')!.disabled = true;
      return;
    }
    const { reference, drawnAreaM2, effectiveAreaM2, discardedAreaM2, parts } = this.drawn;
    this.map.setPartialAreaPreview(reference.geometry);
    const identity = reference.identity;
    document.querySelector('#topo-review')!.innerHTML = facts([
      ['Área dibujada', ha(drawnAreaM2 / 10_000)], ['Área efectiva recortada', ha(effectiveAreaM2 / 10_000)], ['Descartada fuera del padre', ha(discardedAreaM2 / 10_000)],
      ['Partes', String(parts)], ['Finca', identity.finca ?? '—'], ['Suerte', identity.suerte ?? '—'], ...(identity.tablon ? [['Tablón', identity.tablon] as const] : []),
    ]);
    warning.textContent = '';
    this.dialog.querySelector<HTMLButtonElement>('[data-topo-action="export"]')!.disabled = false;
    this.showStep('review');
    this.fitParent();
  }

  private async exportDrawn(): Promise<void> {
    if (!this.drawn) return;
    if (!this.basis || !drawBasisIsCurrent(this.basis, this.store.getState(), this.parent)) {
      document.querySelector('#topo-review-warning')!.textContent = DRAW_STALE_MESSAGE;
      this.dialog.querySelector<HTMLButtonElement>('[data-topo-action="export"]')!.disabled = true;
      return;
    }
    const note = document.querySelector<HTMLTextAreaElement>('#topo-draw-note')?.value;
    try { await this.download(topographyPackage([this.drawn.reference], { note })); this.close(); } catch (error) {
      document.querySelector('#topo-review-warning')!.textContent = error instanceof Error ? error.message : 'No fue posible exportar la referencia.';
    }
  }

  cancel(): void {
    if (this.dialog.dataset.step === 'drawing') this.drawing.cancelSketch();
    if (this.isOpen) this.setStatus('Referencia dibujada descartada · no se guardó nada.');
    this.close();
  }

  private close(): void {
    this.drawn = null; this.basis = null; this.parent = null; this.map.setPartialAreaPreview(null);
    const note = document.querySelector<HTMLTextAreaElement>('#topo-draw-note'); if (note) note.value = ''; // discarded, never stored
    if (this.isOpen) { if (typeof this.dialog.close === 'function') this.dialog.close(); this.dialog.removeAttribute('open'); }
    delete this.dialog.dataset.step;
    if (this.previousTool !== null) { const tool = this.previousTool; this.previousTool = null; if (this.store.getState().activeTool !== tool) this.store.update({ activeTool: tool }, { markDirty: false }); }
    this.render(this.store.getState());
  }

  private showStep(step: 'drawing' | 'review'): void {
    this.dialog.querySelectorAll<HTMLElement>('[data-topo-step]').forEach((element) => { element.hidden = element.dataset.topoStep !== step; });
    this.dialog.dataset.step = step;
  }

  /** One .zip with «<base>.geojson» and «<base>.csv» (JSZip is already part of the app for .casurgeo). */
  private async download(pkg: TopographyPackage): Promise<void> {
    const zip = new JSZip(); zip.file(`${pkg.fileBase}.geojson`, pkg.geojson); zip.file(`${pkg.fileBase}.csv`, pkg.csv);
    this.save(await zip.generateAsync({ type: 'blob', mimeType: 'application/zip' }), `${pkg.fileBase}.zip`);
    this.setStatus(`Referencia exportada · ${pkg.fileBase}.zip · ${pkg.count.toLocaleString('es-NI')} entidad(es) · ${ha(pkg.totalAreaHa)} · ${pkg.fileBase}.geojson + ${pkg.fileBase}.csv`);
  }

  private fitParent(): void {
    const state = this.store.getState(); const model = state.model; const parent = this.parent;
    if (!model || !parent) return;
    const entity = parent.level === 'suerte' ? model.suertes.get(parent.id) : model.tablones.get(parent.id); if (!entity) return;
    const extent = entity.fragmentIds.reduce<number[]>((acc, id) => {
      const e = model.fragmentsById.get(id)?.feature.getGeometry()?.getExtent(); if (!e) return acc;
      return [Math.min(acc[0]!, e[0]!), Math.min(acc[1]!, e[1]!), Math.max(acc[2]!, e[2]!), Math.max(acc[3]!, e[3]!)];
    }, [Infinity, Infinity, -Infinity, -Infinity]);
    if (!extent.every(Number.isFinite)) return;
    const dialog = this.dialog; const target = this.map.map.getTargetElement();
    requestAnimationFrame(() => {
      if (!dialog?.isConnected || !target?.isConnected) return;
      const card = dialog.getBoundingClientRect(); const mapRect = target.getBoundingClientRect();
      this.map.fitInVisibleArea(extent, { bottom: dialog.hasAttribute('open') && card.height ? Math.max(0, mapRect.bottom - card.top) : 0 });
    });
  }
}
